<script setup>
import { onMounted, ref, useTemplateRef } from 'vue'
import { useRoute } from 'vue-router'
import { useDropdown } from '@/composables/useDropdown.js'
import { formatDateLocale } from '@/libs/utils/date.js'
import { canSetPermissions } from '@/libs/utils/permissions.js'
import { buildFilterQuery } from '@/libs/utils.js'
import { getCompiledPolls } from '@/services/compiledPollService.js'

import Paginazione from '@/components/Generics/Paginations/Paginazione.vue'
import Loading from '@/components/Generics/Loadings/Loading.vue'
import FiltriRicercaCompiledPoll from '@/components/CompiledPoll/Common/FiltriRicercaCompiledPoll.vue'

import { client } from '@/services/fetch.js'
import { toast } from '@/services/toast.js'
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import TranslationProvider from '@/provider/translations.js'
import { useFiltersStore, usePageNumberStore } from '@/services/store.js'
import {
  faEye,
  faPencil,
  faEllipsisVertical,
  faKey,
  faArrowsTurnToDots,
  faTrash
} from '@digitalpa/pro-solid-svg-icons'

library.add(
  faPencil,
  faEye,
  faEllipsisVertical,
  faKey,
  faArrowsTurnToDots,
  faTrash
)

const route = useRoute()
const pageNumberStore = usePageNumberStore()
const filtersStore = useFiltersStore()
const { dropDownToggleF, hideDropdowns } = useDropdown()

const polls = ref({
  data: [],
  pagination: {},
  columns: []
})
const isLoading = ref(true)
const currentPage = ref(1)

const modalConfirmChangeState = useTemplateRef('modal-confirm-change-state')
const currCompiledPoll = ref(null)
const currTransitionToApply = ref(null)

const modalConfirmDelete = useTemplateRef('modal-confirm-delete')
const currCompiledPollToDelete = ref(null)
const currentFilters = ref('')
const tableList = useTemplateRef('tableList')

onMounted(async () => {
  await applicaFiltri(pageNumberStore.getPage(route.name))
  await showFiltersHandler(await filtersStore.getShowCard())
})

const applicaFiltri = async (page = 1) => {
  isLoading.value = true
  currentPage.value = page

  const filtri = await filtersStore.filters
  currentFilters.value = buildFilterQuery(filtri)

  polls.value = await getCompiledPolls(page, currentFilters.value)
  isLoading.value = false
}

const showFiltersHandler = (showCard) => {
  if (!tableList.value) {
    return;
  }
  if (showCard) {
    tableList.value.classList.add('list-f')
    tableList.value.classList.remove('list')
  } else {
    tableList.value.classList.add('list')
    tableList.value.classList.remove('list-f')
  }
}

const changePage = async page => {
  isLoading.value = true
  currentPage.value = page
  await applicaFiltri(page)
  isLoading.value = false
}

const applyTransition = async (compiledPoll, transition, apply = false) => {
  if (!apply) {
    modalConfirmChangeState.value.classList.add('d-block')
    currCompiledPoll.value = compiledPoll
    currTransitionToApply.value = transition
  } else {
    try {
      const cPoll = currCompiledPoll.value
      const cTransition = currTransitionToApply.value['key']
      let url = `/compiled-poll/api/compiled-poll/${cPoll}/workflow-transitions/${cTransition}`
      await client.post(url)
      await applicaFiltri(currentPage.value)

      const cTransitionLabel = currTransitionToApply.value['labels']['it']
      toast.create((TranslationProvider.getTrans('app.state_changed')).replace('{transition}', cTransitionLabel).replace('{poll}', cPoll), 'success')
    } catch (error) {
      toast.create(TranslationProvider.getTrans('app.apply_transition_error'), 'error')
    }
    currCompiledPoll.value = null
    currTransitionToApply.value = null
    modalConfirmChangeState.value.classList.remove('d-block')
  }
}

const handleDeleteModal = async (compiledPoll, confirm = false) => {
  if (!confirm) {
    modalConfirmDelete.value.classList.add('d-block')
    currCompiledPollToDelete.value = compiledPoll
  } else {
    try {
      const cPoll = currCompiledPollToDelete.value
      let url = `/compiled-poll/api/compiled-poll/${cPoll}`
      await client.delete(url)
      await applicaFiltri(currentPage.value)
      toast.create((TranslationProvider.getTrans('app.delete_success')).replace('{cPoll}', cPoll), 'success')
    } catch (error) {
      toast.create(TranslationProvider.getTrans('app.delete_error'), 'error')
    }
    currCompiledPollToDelete.value = null
    modalConfirmDelete.value.classList.remove('d-block')
  }
}
</script>

<template>
  <div class="container-fluid px-0" @click="hideDropdowns">
    <div class="card">
      <div class="card-body px-0">

        <filtri-ricerca-compiled-poll
          :isLoading="isLoading"
          :current-filters="currentFilters"
          @applicaFiltri="applicaFiltri"
          @showCardChanged="showFiltersHandler"
        ></filtri-ricerca-compiled-poll>

        <loading v-if="isLoading"></loading>

        <div v-else class="table-responsive">
          <table class="table table-striped" ref="tableList">
            <thead>
            <tr>
              <th class=" border-top-0"
                  :class="item['cssClass']"
                  v-for="item in polls.columns"
                  :key="item"
                  v-html="TranslationProvider.getTrans(item.label)"
              >
              </th>
              <th class=" border-top-0 text-end">Azioni</th>
            </tr>
            </thead>
            <tbody>
            <tr v-if="!polls.data.length">
              <td :colspan="polls.columns.length + 1">
                {{ TranslationProvider.getTrans('app.no_result_found')}}
              </td>
            </tr>
            <tr v-for="poll in polls.data" :key="poll.id">
              <td v-for="item in polls.columns" :key="item.id"
                  :class="item['cssClass']"
              >
                <span v-if="item.type === 'date' || item.type === 'datetime'">
                  {{ formatDateLocale(poll[item.field]) }}
                </span>
                <span v-else-if="item.type === 'attribute' && item.fields">
                    <template v-for="[field, cssClass] in Object.entries(item.fields)" :key="field">
                      <span :class="cssClass" v-if="poll['attributes'][field]">
                        <span>{{ poll['attributes'][field] }}</span>
                      </span>
                      <br>
                    </template>
                </span>
                <span v-else-if="item.type === 'attribute' && !item.fields && item.field">
                    {{ poll['attributes'][item.field] }}
                </span>
                <span v-else-if="item.field === 'campaignData.title'">
                    {{ poll.campaignData?.title }}
                </span>
                <span v-else>{{ poll[item.field] }}</span>
              </td>
              <td class="text-end">
                <div class="d-flex justify-content-end">

                  <router-link
                    v-if="
                        poll['enabledActions'] &&
                        poll['enabledActions']['update']
                      "
                    :to="{ name: 'admin/CompiledPoll--edit', params: { id: poll.id } }"
                  >
                    <button class="btn btn-primary btn-sm ms-2"
                            :title="TranslationProvider.getTrans('app.compila')"
                    >
                      <font-awesome-icon icon="fa-pencil" />
                    </button>
                  </router-link>

                  <router-link
                    v-if="
                        poll['enabledActions'] &&
                        poll['enabledActions']['update']
                      "
                    :to="{ name: 'admin/CompiledPoll--detail', params: { id: poll.id } }"
                  >
                    <button
                      class="btn btn-primary btn-sm ms-2"
                      :title="TranslationProvider.getTrans('app.show_compilazione')"
                    >
                      <font-awesome-icon icon="fa-eye" />
                    </button>
                  </router-link>

                  <router-link
                    v-if="true"
                    :to="{
                        name: 'admin/Permissions',
                        params: { id: poll.id, type: 'compiled-poll' },
                      }"
                  >
                    <button
                      class="btn btn-primary btn-sm ms-2"
                      :title="TranslationProvider.getTrans('app.assign_permissions')"
                      v-if="canSetPermissions"
                    >
                      <font-awesome-icon icon=" fa-key" />
                    </button>
                  </router-link>

                  <div
                    class="dropdown ms-2"
                    v-if="poll['enabledTransitions'].length"
                  >
                    <button
                      class="btn btn-sm btn-primary dropdown-toggle-b"
                      :title="TranslationProvider.getTrans('app.change_state')"
                      @click="dropDownToggleF"
                      ref="dropdown-toggle"
                      type="button"
                      data-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <font-awesome-icon
                        icon="fa-arrows-turn-to-dots"
                        class="dropdown-toggle-icon"
                      />
                    </button>
                    <ul class="dropdown-menu dropdown-menu-right">
                      <li>
                        <a
                          class="dropdown-item"
                          href="#"
                          v-for="et in poll['enabledTransitions']"
                          :key="et.key"
                          @click="applyTransition(poll.id, et, false)"
                        >
                          {{ et['labels']['it'] }}
                        </a>
                      </li>
                    </ul>
                  </div>

                  <button
                    v-if="
                        poll['enabledActions'] &&
                        poll['enabledActions']['delete']
                      "
                    class="btn btn-danger btn-sm ms-2"
                    :title="TranslationProvider.getTrans('app.delete_compilazione')"
                    @click="handleDeleteModal(poll.id)"
                  >
                    <font-awesome-icon icon="fa-trash" />
                  </button>

                </div>
              </td>
            </tr>
            </tbody>
          </table>
          <div
            v-if="Math.ceil(parseInt(polls.pagination.total_items) / parseInt(polls.data.length)) > 1"
            class="d-flex justify-content-end m-2"
          >
            <Paginazione
              :total-pages="
                Math.ceil(
                  polls.pagination.total_items /
                    polls.pagination.items_per_page,
                )
              "
              @page-changed="changePage"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
  <div
    class="modal"
    tabindex="-1"
    ref="modal-confirm-change-state"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Aggiornamento stato compilazione</h5>
          <button
            type="button"
            class="btn-close border-0 bg-white"
            data-bs-dismiss="modal"
            aria-label="Close"
            @click="
              () => {
                modalConfirmChangeState.classList.remove('d-block')
              }
            "
          ></button>
        </div>
        <div class="modal-body" v-if="currCompiledPoll">
          <p>
            Alla compilazione <b>{{ currCompiledPoll }}</b> verrà applicato il
            cambio di stato "<b>{{ currTransitionToApply['labels']['it'] }}</b
          >", procedere?
          </p>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
            @click="
              () => {
                modalConfirmChangeState.classList.remove('d-block')
              }
            "
          >
            Annulla
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="
              applyTransition(currCompiledPoll, currTransitionToApply, true)
            "
          >
            Procedi
          </button>
        </div>
      </div>
    </div>
  </div>
  <div
    class="modal"
    tabindex="-1"
    ref="modal-confirm-delete"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Eliminare la compilazione?</h5>
          <button
            type="button"
            class="btn-close border-0 bg-white"
            data-bs-dismiss="modal"
            aria-label="Close"
            @click="
              () => {
                modalConfirmDelete.classList.remove('d-block')
              }
            "
          ></button>
        </div>
        <div class="modal-body" v-if="currCompiledPollToDelete">
          <p>
            Verrà eliminata la compilazione "<b>{{
              currCompiledPollToDelete
            }}</b
          >", procedere?
          </p>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
            @click="
              () => {
                modalConfirmDelete.classList.remove('d-block')
              }
            "
          >
            Chiudi
          </button>
          <button
            type="button"
            class="btn btn-danger"
            @click="handleDeleteModal(currCompiledPoll, true)"
          >
            Elimina
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
:root,
:host {
  @extend :root;
}

[class*='fa-'] {
  width: 0.9rem;
}

.fa-ellipsis-vertical {
  width: 0.2rem;
}
</style>
